import React from 'react';

/**
 * Higher-order component (HOC) that validates the study ID from route params
 * before rendering the wrapped component. Similar to RequireAuth middleware pattern.
 *
 * Parses match.params.id to an integer and checks for NaN. Invalid IDs (those
 * that cannot be parsed, like "invalid" or "abc") render an error message instead
 * of the wrapped component. Valid IDs are passed as a studyId prop.
 *
 * This prevents cascading API errors (404s, /studies/undefined requests) by
 * failing fast at the route level before components mount.
 *
 * Note: parseInt handles trailing characters like "123+" by stripping them
 * (returns 123), which fixes the original URL-encoded space bug.
 *
 * @param {React.ComponentType} Component - The component to wrap
 * @returns {React.ComponentType} Wrapped component with study ID validation
 *
 * @example
 * // In routes configuration
 * <Route
 *     path="/studies/:id"
 *     component={RequireAuth(RequireValidStudyId(StudyPage))}
 * />
 */
export default function RequireValidStudyId(Component) {
    return function ValidatedStudyComponent(props) {
        const rawId = props.match.params.id;
        const studyId = parseInt(rawId, 10);

        // Validate: must be a valid integer and match when converted back to string
        if (isNaN(studyId) || String(studyId) !== rawId) {
            return (
                <div className="w-full p-10 text-center" role="alert">
                    <p>Unable to load study. Please check the study ID and try again.</p>
                </div>
            );
        }

        return <Component {...props} studyId={studyId} />;
    };
}
